  // Variant Select using Details > Summary on PDP
  class VariantDetailsSelect extends HTMLElement {
    constructor() {
      super();
      this.detailsElement = this.querySelector('details');
      this.summaryElement = this.querySelector('summary');
      this.selectedSizeSpan = this.querySelector('[id^="selected-size-"]');
      this.hiddenSelect = this.querySelector('select');
      this.optionsList = this.querySelector('ul');
      this.optionItems = this.querySelectorAll('li');
      
      // Store handler references for proper cleanup
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this._onHiddenSelectChange = () => {
        this.syncAriaSelectedStates();
        if (this.hiddenSelect.value) {
          this.selectedSizeSpan.textContent = this.hiddenSelect.value;
        }
      };
      this.onOptionsListClick = this.handleOptionsListClick.bind(this);
    }

    connectedCallback() {
      this.initDetailsSelect();
      this.parentVariantRadios = this.closest('variant-radios');
    }

    disconnectedCallback() {
      // Remove document-level listener
      document.removeEventListener('click', this.onDocumentClick);
      
      // Remove hidden select listener
      if (this.hiddenSelect) {
        this.hiddenSelect.removeEventListener('change', this._onHiddenSelectChange);
      }
      
      // Remove delegated options list listener
      if (this.optionsList) {
        this.optionsList.removeEventListener('click', this.onOptionsListClick);
      }
    }

    initDetailsSelect() {
      // Ensure the options list has proper role for screen readers
      if (this.optionsList && !this.optionsList.hasAttribute('role')) {
        this.optionsList.setAttribute('role', 'listbox');
      }

      if (this.hiddenSelect && this.hiddenSelect.value) {
        this.selectedSizeSpan.textContent = this.hiddenSelect.value;
      }

      // Initialize aria-selected states to match the current selection
      if (this.hiddenSelect) {
        this.syncAriaSelectedStates();
      }

      // Attach delegated click listener to options list
      if (this.optionsList) {
        this.optionsList.addEventListener('click', this.onOptionsListClick);
      }

      // Listen for external changes to the hidden select using stored handler
      if (this.hiddenSelect) {
        this.hiddenSelect.addEventListener('change', this._onHiddenSelectChange);
      }

      // Listen for document clicks using stored handler
      document.addEventListener('click', this.onDocumentClick);
    }

    // Helper method to get current option items (DRY principle)
    getCurrentOptionItems() {
      return this.optionsList ? this.optionsList.querySelectorAll('li') : this.querySelectorAll('li');
    }

    // Helper method to ensure proper role attribute (DRY principle)
    ensureOptionRole(item) {
      if (!item.hasAttribute('role')) {
        item.setAttribute('role', 'option');
      }
    }

    // Helper method to set ARIA selected state (DRY principle)
    setAriaSelected(item, isSelected) {
      if (item.getAttribute('role') === 'option') {
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      }
    }

    syncAriaSelectedStates() {
      // Return early if hiddenSelect is not available
      if (!this.hiddenSelect) {
        return;
      }
      
      const selectedValue = this.hiddenSelect.value;
      const currentOptionItems = this.getCurrentOptionItems();
      
      currentOptionItems.forEach(item => {
        this.ensureOptionRole(item);
        const isSelected = item.dataset.value === selectedValue;
        this.setAriaSelected(item, isSelected);
      });
    }

    handleDocumentClick(event) {
      if (!this.detailsElement.contains(event.target)) {
        this.detailsElement.removeAttribute('open');
      }
    }
    handleOptionsListClick(event) {
      // Find the closest li element (option) from the clicked target
      const option = event.target.closest('li');
      
      // Only proceed if we clicked on a valid option within this options list
      if (!option || !this.optionsList.contains(option)) {
        return;
      }
      
      if (option.getAttribute('aria-disabled') !== 'true') {
        const value = option.dataset.value;
        this.selectedSizeSpan.textContent = value;
        this.hiddenSelect.value = value;

        // Update aria-selected for all options using fresh query
        const currentOptionItems = this.getCurrentOptionItems();
        currentOptionItems.forEach(item => {
          this.setAriaSelected(item, false);
        });
        
        // Ensure the selected option has proper role and aria-selected
        this.ensureOptionRole(option);
        this.setAriaSelected(option, true);

        this.detailsElement.removeAttribute('open');

        // Trigger change event on hidden select
        this.hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));

        // Notify the parent VariantRadios component
        if (this.parentVariantRadios) {
          this.parentVariantRadios.onVariantChange();
        }
      }
    }
  }

  customElements.define('variant-details-select', VariantDetailsSelect);
